import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Day-granularity presets. All returned as {from: Date, to: Date} (local days,
// interpreted as UTC days by the caller via `rangeToParams`).
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export const todayRange = () => { const t = startOfDay(new Date()); return { from: t, to: t }; };

const PRESETS = [
  { label: "Today", get: () => todayRange() },
  { label: "Yesterday", get: () => { const y = startOfDay(addDays(new Date(), -1)); return { from: y, to: y }; } },
  { label: "Last 7 days", get: () => ({ from: startOfDay(addDays(new Date(), -6)), to: startOfDay(new Date()) }) },
  { label: "Last 30 days", get: () => ({ from: startOfDay(addDays(new Date(), -29)), to: startOfDay(new Date()) }) },
  { label: "This month", get: () => { const n = new Date(); return { from: startOfDay(new Date(n.getFullYear(), n.getMonth(), 1)), to: startOfDay(n) }; } },
];

// Convert a {from,to} of local days into UTC day-boundary ISO strings that match
// how CDR start_time is stored (…+00:00). No trailing 'Z' so string comparison is exact.
export const rangeToParams = (range) => {
  if (!range?.from) return {};
  const d = (x) => format(x, "yyyy-MM-dd");
  const to = range.to || range.from;
  return { start_from: `${d(range.from)}T00:00:00`, start_to: `${d(to)}T23:59:59.999999` };
};

const label = (range) => {
  if (!range?.from) return "Select dates";
  const f = format(range.from, "MMM d, yyyy");
  if (!range.to || format(range.to, "yyyy-MM-dd") === format(range.from, "yyyy-MM-dd")) return f;
  return `${f} — ${format(range.to, "MMM d, yyyy")}`;
};

export default function DateRangePicker({ value, onChange, testid = "date-range" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`${testid}-trigger`}
          className="h-9 justify-start rounded-md font-normal text-sm w-full">
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          <span className="truncate">{label(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex sm:flex-col gap-1 p-3 border-b sm:border-b-0 sm:border-r border-border flex-wrap">
            {PRESETS.map((p) => (
              <Button key={p.label} variant="ghost" size="sm"
                data-testid={`${testid}-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="justify-start rounded-md text-sm font-normal"
                onClick={() => { onChange(p.get()); setOpen(false); }}>
                {p.label}
              </Button>
            ))}
          </div>
          <Calendar mode="range" numberOfMonths={2} selected={value}
            defaultMonth={value?.from}
            onSelect={(r) => onChange(r || {})} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
